/**
 * Textos e ícones que o cliente pode editar na home do site.
 *
 * Cada tema tem um conjunto próprio de blocos, porque as home dos templates são
 * diferentes: o que existe no Terra (faixa de destaque no topo) não existe no
 * Mercúrio, e vice-versa. Os `default` são exatamente as frases que hoje estão
 * escritas no código de cada template — é o que o cliente vê antes de mexer em
 * nada, e é para onde o botão "Restaurar padrão" volta.
 *
 * `anchor` liga o campo ao bloco correspondente da prévia (Prévia da home).
 */

const searchBlock = (placeholder, mobilePlaceholder) => ({
    key: 'search',
    name: 'Caixa de busca',
    icon: 'search',
    hint: 'A frase cinza que aparece dentro do campo de busca, antes do cliente digitar.',
    fields: [
        {
            key: 'placeholder',
            label: 'Frase no computador',
            type: 'text',
            maxLength: 60,
            default: placeholder,
            hint: 'Ex.: “O que você procura?”',
            anchor: 'search'
        },
        ...(mobilePlaceholder ? [{
            key: 'placeholder_mobile',
            label: 'Frase no celular (mais curta)',
            type: 'text',
            maxLength: 30,
            default: mobilePlaceholder,
            hint: 'No celular o campo é estreito. Se deixar em branco, usamos a frase acima.',
            anchor: 'search'
        }] : [])
    ]
});

const topbarBlock = text => ({
    key: 'topbar',
    name: 'Faixa do topo',
    icon: 'menu',
    hint: 'Faixa fina colorida acima do logo. Deixe o campo em branco para esconder a faixa do site.',
    fields: [
        {
            key: 'text',
            label: 'Frase da faixa',
            type: 'text',
            maxLength: 90,
            default: text,
            hint: 'Uma frase curta que resume o negócio. Ex.: “Especializado na venda de produtos personalizados”.',
            anchor: 'topbar'
        }
    ]
});

/**
 * `links` sai só nos temas que têm onde mostrá-los: Saturno e Terra não têm
 * barra de menu (a `<nav>` está comentada no código deles), então oferecer o
 * campo ali seria prometer uma coisa que o site não faz.
 */
const menuBlock = (allProducts, { links = true } = {}) => ({
    key: 'menu',
    name: 'Menu do site',
    icon: 'menu',
    hint: 'O que aparece no menu: o link do catálogo, as subcategorias e os endereços que você quiser acrescentar.',
    fields: [
        {
            key: 'all_products',
            label: 'Link do catálogo completo',
            type: 'text',
            maxLength: 40,
            default: allProducts,
            hint: 'Ex.: “Todos os produtos”, “Todos os brindes”, “Catálogo”.',
            anchor: 'menu'
        },
        {
            key: 'show_subcategories',
            label: 'Mostrar as subcategorias dentro do menu',
            type: 'toggle',
            default: '',
            hint: 'Desligado, o visitante clica na categoria para ver as de dentro. Ligado, cada categoria já abre a lista com um “Ver todas” em cima.',
            anchor: 'menu'
        }
    ],
    ...(links ? {
        repeater: {
            key: 'links',
            title: 'Links extras',
            itemName: 'Link',
            // Vale para o site todo, não para o tema: endereço do negócio não
            // muda porque o cliente resolveu experimentar outro layout.
            shared: true,
            optional: true,
            max: 6,
            addLabel: 'Acrescentar link',
            hint: 'Aparecem no fim do menu, depois das categorias.',
            fields: [
                { key: 'label', label: 'Texto', type: 'text', maxLength: 30, hint: 'Uma ou duas palavras. Ex.: “Quem somos”.' },
                { key: 'url', label: 'Endereço', type: 'url', maxLength: 300, hint: 'Um endereço completo (https://...) ou uma página da sua loja (/quem-somos).' },
                { key: 'target', label: 'Abrir em nova aba', type: 'toggle', onValue: 'blank' }
            ],
            defaults: []
        },
        anchor: 'menu'
    } : {})
});

const highlightsBlock = (hint, items) => ({
    key: 'highlights',
    name: 'Faixa de vantagens',
    icon: 'destaques',
    hint,
    repeater: {
        key: 'items',
        title: 'As vantagens',
        itemName: 'Vantagem',
        fields: [
            { key: 'icon', label: 'Ícone', type: 'icon' },
            { key: 'title', label: 'Título', type: 'text', maxLength: 26, hint: 'Duas ou três palavras. Ex.: “Frete grátis”.' },
            { key: 'subtitle', label: 'Descrição', type: 'textarea', rows: 2, maxLength: 70, hint: 'Uma explicação curta. Pode usar duas linhas.' }
        ],
        defaults: items
    },
    anchor: 'highlights'
});

const sectionsBlock = fields => ({
    key: 'sections',
    name: 'Títulos das seções',
    icon: 'list-view',
    hint: 'São os títulos que separam as partes da home. Clique em um título na prévia para editá-lo.',
    fields: fields.map(field => ({
        type: 'text',
        maxLength: 60,
        ...field,
        anchor: `section-${field.key}`
    }))
});

/**
 * Único bloco que não é da home: `page` faz a prévia trocar para o desenho da
 * página do produto enquanto ele está aberto.
 */
const productPageBlock = () => ({
    key: 'product',
    name: 'Página do produto',
    icon: 'product',
    page: 'product',
    hint: 'Onde a descrição completa e as especificações técnicas aparecem na página de cada produto.',
    fields: [
        {
            key: 'description_position',
            label: 'Descrição completa',
            type: 'choice',
            default: 'bottom',
            options: [
                { value: 'bottom', label: 'No fim da página', hint: 'Ao lado da foto fica um resumo com o link “Ver descrição completa”.' },
                { value: 'side', label: 'Ao lado da foto', hint: 'O texto inteiro aparece junto do nome do produto, antes do orçamento.' }
            ],
            anchor: 'product'
        },
        {
            key: 'specs_position',
            label: 'Especificações técnicas',
            type: 'choice',
            default: 'bottom',
            options: [
                { value: 'bottom', label: 'No fim da página', hint: 'Ao lado da foto fica o link “Ver especificações”.' },
                { value: 'side', label: 'Ao lado da foto', hint: 'O texto inteiro aparece junto do nome do produto, logo abaixo da descrição.' }
            ],
            anchor: 'product'
        }
    ]
});

const DEFAULT_HIGHLIGHTS_HINT ='Os quatro motivos para comprar com você, logo abaixo do banner principal. Cada um tem ícone, título e descrição.';

const THEMES = {
    'theme-1-jupter': {
        name: 'Júpter',
        blocks: [
            searchBlock('O que você procura?'),
            menuBlock('Todos os produtos'),
            highlightsBlock(DEFAULT_HIGHLIGHTS_HINT, [
                { icon: 'ClipboardList', title: 'Orçamento', subtitle: 'Receba sua cotação\nem poucos passos' },
                { icon: 'Handshake', title: 'Atendimento', subtitle: 'Equipe especializada\npronta para ajudar' },
                { icon: 'Star', title: 'Qualidade', subtitle: 'Produtos que valorizam\nsua marca' },
                { icon: 'ShieldCheck', title: 'Confiança', subtitle: 'Processo transparente\ndo orçamento ao pedido' }
            ]),
            sectionsBlock([
                { key: 'featured', label: 'Produtos em destaque', default: 'Destaques' },
                { key: 'recent', label: 'Produtos mais novos', default: 'Lançamentos' },
                { key: 'logos', label: 'Logos dos clientes', default: 'Nossos clientes' },
                { key: 'testimonials', label: 'Depoimentos', default: 'Depoimentos' }
            ]),
            productPageBlock()
        ],
        layout: [
            { type: 'header', block: 'search' },
            { type: 'menu', block: 'menu' },
            { type: 'banner', caption: 'Banner principal' },
            { type: 'highlights', block: 'highlights' },
            { type: 'section', block: 'sections', field: 'featured' },
            { type: 'banner', caption: 'Banners' },
            { type: 'section', block: 'sections', field: 'recent' },
            { type: 'text', caption: 'Quem somos (editado em Páginas)' },
            { type: 'logos', block: 'sections', field: 'logos' },
            { type: 'testimonials', block: 'sections', field: 'testimonials' }
        ]
    },

    'theme-2-mercurio': {
        name: 'Mercúrio',
        blocks: [
            topbarBlock('Especializado na venda de produtos personalizados'),
            searchBlock('O que você está procurando?', 'Procurar brinde'),
            menuBlock('Todos os produtos'),
            sectionsBlock([
                { key: 'recent', label: 'Produtos mais novos', default: 'Lançamentos' },
                { key: 'featured', label: 'Produtos em destaque', default: 'Destaques' },
                { key: 'logos', label: 'Logos dos clientes', default: 'Clientes Atendidos' },
                { key: 'testimonials', label: 'Depoimentos', default: 'Depoimentos' }
            ])
        ],
        layout: [
            { type: 'topbar', block: 'topbar', field: 'text' },
            { type: 'header', block: 'search' },
            { type: 'menu', block: 'menu' },
            { type: 'banner', caption: 'Banner principal' },
            { type: 'categories', caption: 'Categorias' },
            { type: 'section', block: 'sections', field: 'recent' },
            { type: 'section', block: 'sections', field: 'featured' },
            { type: 'logos', block: 'sections', field: 'logos' },
            { type: 'testimonials', block: 'sections', field: 'testimonials' }
        ]
    },

    'theme-3-saturno': {
        name: 'Saturno',
        blocks: [
            searchBlock('Qual brinde você procura?', 'Procurar brinde'),
            menuBlock('Todos os brindes', { links: false }),
            {
                key: 'curated',
                name: 'Seleções especiais',
                icon: 'image',
                hint: 'O bloco de vitrines logo abaixo do menu, ao lado do filtro de preço.',
                fields: [
                    {
                        key: 'title',
                        label: 'Título',
                        type: 'text',
                        maxLength: 50,
                        default: 'Seleções especiais',
                        anchor: 'curated'
                    },
                    {
                        key: 'subtitle',
                        label: 'Frase de apoio',
                        type: 'text',
                        maxLength: 90,
                        default: 'Selecionamos alguns itens para momentos específicos',
                        hint: 'Aparece em letra menor, abaixo do título.',
                        anchor: 'curated'
                    }
                ]
            },
            highlightsBlock(DEFAULT_HIGHLIGHTS_HINT, [
                { icon: 'Truck', title: 'Envio seguro', subtitle: 'Diversas modalidades disponíveis' },
                { icon: 'Calculator', title: 'Orçamento rápido', subtitle: 'Orçamento rápido e detalhado no seu e-mail' },
                { icon: 'MapPin', title: 'Logística eficiente', subtitle: 'Processos eficientes para entregas rápidas e seguras' },
                { icon: 'CreditCard', title: 'Pague fácil', subtitle: 'Condições facilitadas e especiais' }
            ]),
            sectionsBlock([
                { key: 'categories', label: 'Vitrines por categoria', default: 'Temos o brinde ideal para sua necessidade' },
                { key: 'featured', label: 'Produtos em destaque', default: 'Conheça os brindes mais vendidos' },
                { key: 'recent', label: 'Produtos mais novos', default: 'Brindes que acabaram de chegar' },
                { key: 'testimonials', label: 'Depoimentos', default: 'Confira o que dizem nossos clientes' },
                { key: 'logos', label: 'Logos dos clientes', default: 'Empresas que confiam em nós' }
            ])
        ],
        layout: [
            { type: 'header', block: 'search' },
            { type: 'menu', block: 'menu' },
            { type: 'curated', block: 'curated' },
            { type: 'highlights', block: 'highlights' },
            { type: 'section', block: 'sections', field: 'categories' },
            { type: 'section', block: 'sections', field: 'featured' },
            { type: 'section', block: 'sections', field: 'recent' },
            { type: 'testimonials', block: 'sections', field: 'testimonials' },
            { type: 'logos', block: 'sections', field: 'logos' }
        ]
    },

    'theme-4-terra': {
        name: 'Terra',
        blocks: [
            searchBlock('Qual produto você procura?', 'Procurar brinde'),
            menuBlock('Todos os produtos', { links: false }),
            {
                key: 'hero',
                name: 'Frase sobre o banner',
                icon: 'design',
                hint: 'A frase grande escrita em cima da imagem do topo. É a primeira coisa que o visitante lê.',
                fields: [
                    {
                        key: 'kicker',
                        label: 'Linha de cima (menor)',
                        type: 'text',
                        maxLength: 40,
                        default: 'Colaboradores e clientes',
                        hint: 'Texto pequeno em maiúsculas, acima da frase principal.',
                        anchor: 'hero'
                    },
                    {
                        key: 'title',
                        label: 'Frase principal (grande)',
                        type: 'textarea',
                        rows: 2,
                        maxLength: 60,
                        default: 'Engajados com\nsua marca',
                        hint: 'Use duas linhas curtas para o texto não ficar apertado no celular.',
                        anchor: 'hero'
                    }
                ]
            },
            highlightsBlock(DEFAULT_HIGHLIGHTS_HINT, [
                { icon: 'Truck', title: 'Envio seguro', subtitle: 'Diversas modalidades disponíveis' },
                { icon: 'Calculator', title: 'Orçamento rápido', subtitle: 'Orçamento rápido e detalhado no seu e-mail' },
                { icon: 'MapPin', title: 'Logística eficiente', subtitle: 'Processos eficientes para entregas rápidas e seguras' },
                { icon: 'CreditCard', title: 'Pague fácil', subtitle: 'Condições facilitadas e especiais' }
            ]),
            sectionsBlock([
                { key: 'ideal', label: 'Vitrines por tipo de produto', type: 'textarea', rows: 2, default: 'Escolha seu tipo de\nproduto ideal' },
                { key: 'recent', label: 'Produtos mais novos', default: 'Lançamentos' },
                { key: 'featured', label: 'Produtos em destaque', default: 'Destaques' },
                { key: 'logos', label: 'Logos dos clientes', default: 'Clientes Atendidos' },
                { key: 'testimonials', label: 'Depoimentos', default: 'Depoimentos' }
            ])
        ],
        layout: [
            { type: 'header', block: 'search' },
            { type: 'menu', block: 'menu' },
            { type: 'hero', block: 'hero' },
            { type: 'categories', caption: 'Vitrines em destaque' },
            { type: 'highlights', block: 'highlights' },
            { type: 'section', block: 'sections', field: 'ideal' },
            { type: 'section', block: 'sections', field: 'recent' },
            { type: 'section', block: 'sections', field: 'featured' },
            { type: 'logos', block: 'sections', field: 'logos' },
            { type: 'testimonials', block: 'sections', field: 'testimonials' }
        ]
    }
};

export const SHARED_KEY = '_shared';

export const isThemeEditable = slug => Boolean(THEMES[slug]);

export const getThemeSchema = slug => THEMES[slug] ?? null;

export const getThemeBlocks = slug => THEMES[slug]?.blocks ?? [];

export const getThemeLayout = slug => THEMES[slug]?.layout ?? [];

export const getBlock = (slug, blockKey) => getThemeBlocks(slug).find(block => block.key === blockKey) ?? null;

/**
 * Quem digita "google.com" quer um site, não um caminho da loja. Sem o esquema
 * o backend recusaria o endereço inteiro, então completamos aqui.
 */
const normalizeUrl = url => {
    const value = (url ?? '').trim();

    if(value === '' || value.startsWith('/') || /^[a-z][a-z0-9+.-]*:/i.test(value)) return value;

    return `https://${value}`;
}

/**
 * Linha incompleta não vai para o banco: um link sem texto ou sem endereço só
 * renderia um buraco no menu do site.
 */
const completeItems = (repeater, items) => (Array.isArray(items) ? items : [])
    .map(item => ({ ...item, ...(item.url !== undefined ? { url: normalizeUrl(item.url) } : {}) }))
    .filter(item => (item.label ?? '').trim() !== '' && (item.url ?? '').trim() !== '');

/** Item em branco, para quando o cliente acrescenta uma linha ao repetidor. */
export const getEmptyItem = repeater => repeater.fields.reduce((item, field) => {
    item[field.key] = '';
    return item;
}, {});

export const getBlockDefaults = block => {
    const values = {};

    (block.fields ?? []).forEach(field => {
        values[field.key] = field.default ?? '';
    });

    if(block.repeater){
        values[block.repeater.key] = block.repeater.defaults.map(item => ({ ...item }));
    }

    return values;
}

export const getThemeDefaults = slug => getThemeBlocks(slug).reduce((values, block) => {
    values[block.key] = getBlockDefaults(block);
    return values;
}, {});

/**
 * Une o que está salvo com os padrões do tema. Campo salvo vazio é intencional
 * (é assim que o cliente esconde a faixa do topo, por exemplo), então só cai no
 * padrão quando a chave não existe.
 *
 * `shared` é o que não depende do tema — hoje só os links do menu. Vem de outra
 * gaveta no banco e é misturado aqui, para a tela lidar com um objeto só.
 */
export const mergeThemeContent = (slug, saved, shared) => {
    const merged = {};

    getThemeBlocks(slug).forEach(block => {
        const defaults = getBlockDefaults(block);
        const savedBlock = saved?.[block.key] ?? {};

        merged[block.key] = { ...defaults };

        (block.fields ?? []).forEach(field => {
            if(savedBlock[field.key] !== undefined){
                merged[block.key][field.key] = savedBlock[field.key];
            }
        });

        if(block.repeater){
            const repeater = block.repeater;
            const savedItems = (repeater.shared ? shared?.[block.key]?.[repeater.key] : savedBlock[repeater.key]);

            // Repetidor opcional (links do menu) não tem padrão: a lista é
            // exatamente o que o cliente montou, inclusive vazia.
            merged[block.key][repeater.key] = repeater.optional
                ? (Array.isArray(savedItems) ? savedItems.map(item => ({ ...item })) : [])
                : defaults[repeater.key].map((item, index) => ({
                    ...item,
                    ...(Array.isArray(savedItems) ? (savedItems[index] ?? {}) : {})
                }));
        }
    });

    return merged;
}

export const countBlockChanges = (block, values) => {
    const defaults = getBlockDefaults(block);
    let changes = 0;

    (block.fields ?? []).forEach(field => {
        if((values?.[field.key] ?? '') !== (defaults[field.key] ?? '')) changes++;
    });

    return changes + countBlockRepeaterChanges(block, values);
}

/**
 * Só o que o cliente mudou vai para o banco, separado em duas gavetas: o que é
 * do tema e o que é do site.
 *
 * Os padrões continuam sendo as frases escritas no código de cada template: se
 * gravássemos tudo, um ajuste de texto feito no template nunca chegaria a quem
 * já abriu esta tela uma vez. Campo salvo vazio é diferente de campo ausente —
 * é assim que se esconde a faixa do topo —, por isso a comparação é com o
 * padrão, não com string vazia.
 *
 * O repetidor vai inteiro quando qualquer item muda: a posição é o que liga o
 * item ao padrão do template, e meia lista salva ficaria ilegível no banco.
 */
export const diffThemeContent = (slug, content) => {
    const theme = {};
    const shared = {};

    getThemeBlocks(slug).forEach(block => {
        const defaults = getBlockDefaults(block);
        const values = content?.[block.key] ?? {};
        const changed = {};

        (block.fields ?? []).forEach(field => {
            const value = values[field.key] ?? '';

            if(value !== (defaults[field.key] ?? '')) changed[field.key] = value;
        });

        const repeater = block.repeater;

        // Lista opcional não tem padrão para comparar, e precisa ser gravada até
        // quando ficou vazia — senão apagar o último link não apaga nada.
        if(repeater?.optional){
            shared[block.key] = { [repeater.key]: completeItems(repeater, values[repeater.key]) };
        } else if(repeater && countBlockRepeaterChanges(block, values) > 0){
            changed[repeater.key] = (values[repeater.key] ?? []).map(item => ({ ...item }));
        }

        if(Object.keys(changed).length > 0) theme[block.key] = changed;
    });

    return { theme, shared };
}

const countBlockRepeaterChanges = (block, values) => {
    if(!block.repeater) return 0;

    const repeater = block.repeater;
    const items = values?.[repeater.key] ?? [];

    if(repeater.optional) return completeItems(repeater, items).length;

    const defaults = getBlockDefaults(block);
    let changes = 0;

    defaults[repeater.key].forEach((defaultItem, index) => {
        repeater.fields.forEach(field => {
            if((items[index]?.[field.key] ?? '') !== (defaultItem[field.key] ?? '')) changes++;
        });
    });

    return changes;
}
